import {observer} from "mobx-react-lite";
import * as React from "react";
import IocDataTable from "./IocDataTable";
import DateRow from "../DateRow";
import RelatedRecordsTable from "./RelatedRecordsTable";
import {Badge, Col, OverlayTrigger, Popover, Row, Tab, Tabs} from "react-bootstrap";
import Card from "react-bootstrap/Card"
import {Ioc} from "../../models/ioc";
import UnavailableFeature from "./UnavailableFeature";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons"
import {countryCode} from "emoji-flags"
import DnsResults from "./DnsResults";
import WhoisIPResults from "./WhoisIPResults";
import WhoisDomainResults from "./WhoisDomainResults";
import TargetRow from "../TargetRow";

const riskOverlay = <Popover id="riskDescriptionTooltip">
    <Popover.Content>
        <p>
            The Risk level is the likelihood that traffic from or to the IP addresses in the target (or resolving the domains/IPs) is malicious.
        </p>
    </Popover.Content>
</Popover>

const confidenceOverlay = <Popover id="confidenceDescriptionTooltip">
    <Popover.Content>
        <p>
            The confidence level indicates how confident ThreatSTOP is in the classification of the IP address or Domain name as the Threat Type associated with the target.
        </p>
    </Popover.Content>
</Popover>

const severityOverlay = <Popover id="severityDescriptionTooltip">
    <Popover.Content>
        <p>Level of threat posed by this IOC</p>
    </Popover.Content>
</Popover>



const CheckIocData = observer(function CheckIocData(props: { data: Ioc }) {
    const {data} = props

    const targetsLimit = 10

    return <>
        <Row>
            <Col>
                <h2>Showing results for the following IOC: {data.ioc}</h2>

                <p>Current severity associated with this IOC:</p>
                <Row>
                    <Col className="text-left"><Badge>not a threat</Badge></Col>
                    <Col className="text-center"><Badge>malicious</Badge></Col>
                    <Col className="text-right"><Badge>active threat</Badge></Col>
                </Row>
                <div className="danger-indicator">
                    <div className={`danger-indicator-handle danger-indicator-handle-position-${(data.threatStatus.active ? data.threatStatus.severity : 0)}`}>
                        <div className="danger-indicator-handle-top"/>
                        <div className="danger-indicator-handle-middle"/>
                        <div className="danger-indicator-handle-bottom"/>
                    </div>
                </div>
            </Col>

        </Row>
        <Row>
            <Col xs={5}>
                <Card className="status-card threat-status-outer">
                    <Card className="threat-status-inner" border={data.threatStatus.active ? "danger" : "info"}>
                        <Card.Header>Threat Status</Card.Header>
                        <Card.Body>
                            <p>Current Threat Status: <strong>{data.threatStatus.active ? 'Active Threat' : 'Not a Threat'}</strong></p>
                        </Card.Body>
                    </Card>
                    <Card className="threat-status-score" border={data.threatStatus.active ? "danger" : "info"}>
                        <Card.Header>{data.active.length === 0 ? 'IOC Score when IOC was last active' : 'IOC Score'}</Card.Header>
                        <Card.Body>
                            <OverlayTrigger placement="bottom" overlay={severityOverlay}>
                                <p>Severity Level: {data.threatStatus.severity === -1 ? "Unknown" : `${data.threatStatus.severity} of 5`}</p>
                            </OverlayTrigger>
                            <OverlayTrigger placement="bottom" overlay={confidenceOverlay}>
                                <p>Confidence Level: {data.threatStatus.confidence === -1 ? "Unknown" : `${data.threatStatus.confidence} of 5`}</p>
                            </OverlayTrigger>
                            <OverlayTrigger placement="bottom" overlay={riskOverlay}>
                                <p>Risk Level: {data.threatStatus.risk === -1 ? "Unknown" : `${data.threatStatus.risk} of 5`}</p>
                            </OverlayTrigger>
                        </Card.Body>
                    </Card>
                </Card>

            </Col>
            <Col xs={7}>
                <Card className="status-card ioc-info" border={data.threatStatus.active ? "danger" : "info"}>
                    <Card.Header>IOC Info</Card.Header>
                    <Card.Body>
                        {data.info.country !== 'Unknown' && <p>Country of Origin: {data.info.country} {countryCode(data.info.country) ? countryCode(data.info.country).emoji : ''}</p>}
                        <p>First Seen: <DateRow timestamp={data.info.firstSeen}/></p>
                        <p>Last Seen: {data.threatStatus.active ? 'Now' : <DateRow timestamp={data.info.lastSeen}/>}</p>
                        <p>Blocked connections to this IOC in the last 7 days: {data.info.hits}</p>
                        <p>Active and Historical Targets:
                            {[...data.active, ...data.history].slice(0, targetsLimit).map((v, i) => <TargetRow key={i} target={v.blocker} />)}
                            {data.info.targets.length > targetsLimit ?
                            <OverlayTrigger
                                transition={false}
                                placement="left"
                                overlay={
                                    <Popover id="moreTargetsTooltip">
                                        <Popover.Content>
                                            {data.info.targets.slice(targetsLimit).map(v =>
                                                <Badge key={v}>{v}</Badge>
                                            )}
                                        </Popover.Content>
                                    </Popover>
                                }
                            >
                                <Badge variant="light">
                                    and {data.info.targets.length - targetsLimit} more
                                </Badge>
                            </OverlayTrigger> : ''
                        }
                        </p>
                        <p className="target-legend">
                            <Badge variant="danger">5+</Badge> Danger Level 5 or higher{'  '}
                            <Badge variant="warning">3-4</Badge> Danger Level 3–4{'  '}
                            <Badge variant="secondary">≤ 2</Badge> Danger Level 2 or lower
                        </p>

                    </Card.Body>
                </Card>
            </Col>
        </Row>
        <Row>
            <Col className="ioc-data-tabs">
                <Tabs defaultActiveKey="activeTargets" id="iocData">
                    <Tab eventKey="activeTargets" title="Active Targets">
                        <p>This section shows active known records from different targets.</p>
                        <IocDataTable data={data.active}/>
                    </Tab>
                    <Tab eventKey="historicalTargets" title="Historical Targets">
                        <p>This section shows no longer active historical records.</p>
                        <IocDataTable data={data.history}/>
                    </Tab>
                    <Tab eventKey="related" title="Related Records">
                        <p>This section shows IP addresses (A records) resolved for the requested domain.</p>
                        <p>It does not perform a complete lookup of all DNS records associated with the domain.</p>
                        <RelatedRecordsTable data={data.related}/>
                    </Tab>
                    <Tab eventKey="whois" title="Whois">
                        {!data.whois && <p>No data</p>}
                        {data.whois && 'domain_name' in data.whois && <WhoisDomainResults data={data.whois} />}
                        {data.whois && 'nets' in data.whois && <WhoisIPResults data={data.whois} />}
                    </Tab>
                    <Tab eventKey="dig" title="DNS Lookup">
                        <DnsResults data={data.dns} />
                    </Tab>
                    <Tab eventKey="pdns" title={<span>PDNS <FontAwesomeIcon icon={faLock} /></span>}>
                        <p>Passive DNS shows the DNS records historically associated with this IP address. This data is only available in the ThreatSTOP customer portal.</p>
                        <UnavailableFeature />
                    </Tab>
                    <Tab eventKey="asn" title={<span>ASN Records <FontAwesomeIcon icon={faLock} /></span>}>
                        <p>ASN Records shows IOCs in the same <a href="https://en.wikipedia.org/wiki/Autonomous_system_(Internet)" target="_blank" rel="noreferrer">Autonomous System</a>.</p>
                        <UnavailableFeature />
                    </Tab>
                </Tabs>
            </Col>
        </Row>
    </>
})

export default CheckIocData